import PropTypes from 'prop-types';
import { createContext, useCallback, useContext, useMemo, useState } from 'react';
// material
import { CssBaseline } from '@material-ui/core';
import { ThemeProvider, createTheme, StyledEngineProvider } from '@material-ui/core/styles';
//
import shape from './shape';
import { getPalette } from './palette';
import typography from './typography';
import GlobalStyles from './globalStyles';
import componentsOverride from './overrides';
import { getShadows, getCustomShadows } from './shadows';

// ----------------------------------------------------------------------

// Read the persisted preference, fall back to 'light'. Wrapped in try/catch
// because localStorage may be unavailable (private browsing, SSR, tests).
const getInitialMode = (override) => {
  if (override === 'light' || override === 'dark') return override;
  try {
    const savedMode = localStorage.getItem('themeMode');
    if (savedMode === 'light' || savedMode === 'dark') return savedMode;
  } catch (e) {
    // localStorage might not be available in some environments
    console.warn('Unable to read themeMode from localStorage:', e);
  }
  return 'light';
};

const persistMode = (mode) => {
  try {
    localStorage.setItem('themeMode', mode);
  } catch (e) {
    console.warn('Unable to save themeMode to localStorage:', e);
  }
};

// Context provides the theme mode + actions to the tree without forcing every
// <ThemeConfig> consumer to be wrapped in a Redux <Provider>. The default value
// keeps 'light' mode working in environments where the provider is absent
// (e.g. component-level unit tests).
const ThemeModeContext = createContext({
  mode: 'light',
  setMode: () => {},
  toggleMode: () => {}
});

export function useThemeMode() {
  return useContext(ThemeModeContext);
}

// Owns the theme mode state and exposes it via Context. The single subscriber
// point keeps consumers decoupled from the storage mechanism (currently
// useState + localStorage); swap to a different mechanism without touching
// any consumer.
ThemeModeProvider.propTypes = {
  children: PropTypes.node,
  initialMode: PropTypes.oneOf(['light', 'dark'])
};

export function ThemeModeProvider({ children, initialMode }) {
  const [mode, setModeState] = useState(() => getInitialMode(initialMode));

  const setMode = useCallback((newMode) => {
    if (newMode !== 'light' && newMode !== 'dark') return;
    setModeState(newMode);
    persistMode(newMode);
  }, []);

  const toggleMode = useCallback(() => {
    setModeState((prev) => {
      const next = prev === 'light' ? 'dark' : 'light';
      persistMode(next);
      return next;
    });
  }, []);

  const value = useMemo(() => ({ mode, setMode, toggleMode }), [mode, setMode, toggleMode]);

  return <ThemeModeContext.Provider value={value}>{children}</ThemeModeContext.Provider>;
}

// ----------------------------------------------------------------------

ThemeConfig.propTypes = {
  children: PropTypes.node
};

export default function ThemeConfig({ children }) {
  const { mode } = useThemeMode();

  const themeOptions = useMemo(
    () => ({
      palette: getPalette(mode),
      shape,
      typography,
      shadows: getShadows(mode),
      customShadows: getCustomShadows(mode)
    }),
    [mode]
  );

  const theme = createTheme(themeOptions);
  theme.components = componentsOverride(theme);

  return (
    <StyledEngineProvider>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <GlobalStyles />
        {children}
      </ThemeProvider>
    </StyledEngineProvider>
  );
}
